import constants from "../../../utils/Constants";
import getInternalStyle from "../../../utils/Styles";
import { getVariant } from "../../../utils/Experiment";
import ChatTooltip from "../components/ChatTooltip";
import { resolveLauncherPosition } from "./launcherPosition";

interface ChatButtonProps {
  isOpen: boolean;
  toggleChatWidget: () => void;
  style: any;
  isMobile: boolean;
  language: string;
  assistant: any;
  assistantChatConfiguration?: any;
  onTeaserClick?: (question: string) => void;
  isEditor?: boolean;
}

// Variant B: the unread badge disappears once the guest has opened the widget
// in this session (an unread that never clears would read as a lie).
const BADGE_CLEARED_KEY = "drastik_widget_badge_cleared";

function showUnreadBadge(isOpen: boolean, isEditor = false) {
  try {
    return (
      getVariant() === "B" &&
      !isOpen &&
      // The backoffice preview always shows the badge (session state from
      // past test opens must not hide what's being configured).
      (isEditor || !sessionStorage.getItem(BADGE_CLEARED_KEY))
    );
  } catch {
    return false;
  }
}

export function clearUnreadBadge() {
  try {
    sessionStorage.setItem(BADGE_CLEARED_KEY, "1");
  } catch {
    // no sessionStorage: badge just stays, chat still works
  }
}

// The launcher's corner and distances come from the widget configuration
// (backoffice, per device); see launcherPosition.ts for the defaults.
function getButtonPosition(isMobile: boolean, style: any) {
  const { side, bottom, offset } = resolveLauncherPosition(
    style?.position,
    isMobile
  );
  return {
    bottom: `${bottom}px`,
    [side]: `${offset}px`,
    width: isMobile ? "60px" : "80px",
    height: isMobile ? "60px" : "80px",
  };
}

export const ChatButton = ({
  isOpen,
  toggleChatWidget,
  style,
  isMobile,
  assistant,
  language,
  assistantChatConfiguration,
  onTeaserClick,
  isEditor = false,
}: ChatButtonProps) => (
  <div
    className={`widget-chat-button${
      resolveLauncherPosition(style?.position, isMobile).side === "left" && !isEditor
        ? " is-left"
        : ""
    } p-1 w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white text-3xl focus:outline-none hover:bg-blue-600 transition-colors duration-200 ease-in-out relative`}
    style={{
      backgroundColor: style.color,
      boxShadow: `0 4px 6px -1px ${style.color}, 0 2px 4px -1px ${style.color}`,
      opacity: !isOpen ? "1" : "0",
      transition: "opacity 0.15s ease-in-out",
      // Editor mode lives inside the backoffice preview frame: anchor to the
      // frame, not the browser viewport.
      position: isEditor ? "absolute" : "fixed",
      // The preview frame always shows the default corner.
      ...getButtonPosition(isMobile, isEditor ? null : style),
      ...(isEditor ? { bottom: "24px", right: "24px" } : {}),
      color: "#fff",
      border: "none",
      borderRadius: "50%",
      textAlign: "center",
      lineHeight: "60px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
    onClick={toggleChatWidget}
  >
    <img
      src={getInternalStyle(assistant?.id, style).open_button.image}
      alt="Bot Avatar"
      className="bot-avatar-open-button"
      style={{
        width: isMobile ? "60px" : "80px",
        height: isMobile ? "60px" : "80px",
        borderRadius: "50%",
        objectFit: "cover",
      }}
    />
    {constants.FEATURE_FLAGS.TOOLTIP && (
      <ChatTooltip
        isMobile={isMobile}
        language={language}
        assistantId={assistant?.id}
        assistantChatConfiguration={assistantChatConfiguration}
        onTeaserClick={onTeaserClick}
      />
    )}
    {showUnreadBadge(isOpen, isEditor) && <div className="unread-badge">1</div>}
    <div
      className="pulse-effect"
      style={{
        position: "absolute",
        bottom: "0px",
        left: "0px",
        width: isMobile ? "12px" : "15px",
        height: isMobile ? "12px" : "15px",
        borderRadius: "50%",
        backgroundColor: "rgba(38, 178, 11)",
        border: "2px solid white",
        boxShadow: "0 0 0 0 rgba(38, 178, 11, 0.4)",
        animation: "pulse 2s infinite",
      }}
    />
  </div>
);
